<template>
  <div class="py-4 space-y-6">
    <!-- Title -->
    <div class="text-center mb-6">
      <h1 class="text-xl font-bold text-foreground mb-2">
        {{ $t('report.clients_card_breakdown_info.title') }}
      </h1>
      <p class="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
        {{ $t('report.clients_card_breakdown_info.description') }}
      </p>
    </div>

    <!-- Row data -->
    <div class="space-y-4">
      <div class="flex items-center gap-2">
        <div class="w-1 h-3.5 rounded-full" style="background: #06C8FF"></div>
        <h2 class="text-sm font-bold uppercase tracking-wide text-foreground">
          {{ $t('report.clients_card_breakdown_info.how_to_read') }}
        </h2>
      </div>

      <!-- Mini illustration: mock card row -->
      <div class="rounded-lg p-3 info-example-bg">
        <div class="relative rounded-md overflow-hidden info-row-bg">
          <div class="absolute left-0 inset-y-0 w-[3px]" style="background: #06C8FF"></div>
          <div class="pl-3 pr-2.5 py-2">
            <div class="flex items-center justify-between mb-1">
              <span class="text-[11px] font-bold text-foreground">VIP</span>
              <span class="text-[11px] font-bold text-foreground">32 <span class="text-[8px] text-muted-foreground/60">{{ $t('report.clients_label').toLowerCase() }}</span></span>
            </div>
            <div class="flex items-center gap-1 flex-wrap">
              <span class="info-chip info-chip--revenue">1 250 &euro;</span>
              <span class="info-chip">&times;1.4</span>
              <span class="info-chip">28.90 &euro;/ticket</span>
              <span class="info-chip">39.06 &euro;/{{ $t('report.clients_label').toLowerCase().slice(0, -1) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="space-y-3">
        <div class="grid grid-cols-2 gap-x-3 gap-y-1.5">
          <div class="flex items-baseline gap-1.5">
            <span class="info-chip info-chip--revenue text-[8px]">CA</span>
            <span class="text-[10px] text-muted-foreground">{{ $t('report.clients_card_breakdown_info.chip_revenue_desc') }}</span>
          </div>
          <div class="flex items-baseline gap-1.5">
            <span class="info-chip text-[8px]">&times;1.4</span>
            <span class="text-[10px] text-muted-foreground">{{ $t('report.clients_card_breakdown_info.chip_frequency_desc') }}</span>
          </div>
          <div class="flex items-baseline gap-1.5">
            <span class="info-chip text-[8px]">/ticket</span>
            <span class="text-[10px] text-muted-foreground">{{ $t('report.clients_card_breakdown_info.chip_ticket_desc') }}</span>
          </div>
          <div class="flex items-baseline gap-1.5">
            <span class="info-chip text-[8px]">/{{ $t('report.clients_label').toLowerCase().slice(0, -1) }}</span>
            <span class="text-[10px] text-muted-foreground">{{ $t('report.clients_card_breakdown_info.chip_member_desc') }}</span>
          </div>
        </div>

        <div class="flex items-start gap-2.5">
          <span class="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style="background: #C4CAD4"></span>
          <div>
            <span class="text-xs font-semibold text-foreground">{{ $t('report.clients_card_breakdown_info.non_member_label') }}</span>
            <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
              {{ $t('report.clients_card_breakdown_info.non_member_desc') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Distribution bars -->
    <div class="space-y-2.5">
      <div class="flex items-center gap-2">
        <div class="w-1 h-3.5 rounded-full" style="background: #06C8FF"></div>
        <h2 class="text-sm font-bold uppercase tracking-wide text-foreground">
          {{ $t('report.clients_card_breakdown_info.bars_section') }}
        </h2>
      </div>

      <div class="info-metric-item">
        <span class="info-metric-label">{{ $t('report.clients_card_breakdown_info.bars_label') }}</span>
        <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
          {{ $t('report.clients_card_breakdown_info.bars_desc') }}
        </p>
      </div>
      <div class="info-metric-item">
        <span class="info-metric-label">{{ $t('report.clients_card_breakdown_info.legend_label') }}</span>
        <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
          {{ $t('report.clients_card_breakdown_info.legend_desc') }}
        </p>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
// Content-only component — no props needed.
// Injected into a BottomSheet from CenterClientsReport.
</script>

<style scoped>
.info-example-bg {
  background: rgba(107, 114, 128, 0.06);
  border: 1px solid rgba(107, 114, 128, 0.1);
}
:is(.dark *).info-example-bg {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.info-row-bg {
  background: rgba(0, 0, 0, 0.02);
}
:is(.dark *).info-row-bg {
  background: rgba(255, 255, 255, 0.03);
}
.info-metric-item {
  padding-left: 0.75rem;
  border-left: 2px solid rgba(107, 114, 128, 0.15);
}
:is(.dark *).info-metric-item {
  border-left-color: rgba(255, 255, 255, 0.1);
}
.info-metric-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--foreground);
}
.info-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 600;
  color: var(--foreground);
  opacity: 0.7;
  background: rgba(0, 0, 0, 0.03);
}
:is(.dark *).info-chip {
  background: rgba(255, 255, 255, 0.05);
}
.info-chip--revenue {
  opacity: 1;
  font-weight: 700;
  color: #009FE3;
  background: rgba(6, 200, 255, 0.08);
}
:is(.dark *).info-chip--revenue {
  color: #06C8FF;
  background: rgba(6, 200, 255, 0.12);
}
</style>
